"use client";

import { ReactNode, createContext, useContext, useMemo } from "react";

import { useFiltersStore } from "../stores/filters.store";
import type { PendingFilters } from "../types/filters.type";

export interface FilterContextValue {
  getSelectedValues(field: string): string[];
  onSelectionChange(field: string, values: string[]): void;
}

export const FilterContext = createContext<FilterContextValue | null>(null);

export function MoreFiltersProvider({ children }: { children: ReactNode }) {
  const pendingFilters = useFiltersStore((s) => s.pendingFilters);
  const updatePendingFilter = useFiltersStore((s) => s.updatePendingFilter);

  const value: FilterContextValue = useMemo(
    () => ({
      getSelectedValues: (field) => {
        if (field === "price") {
          const min = (pendingFilters as PendingFilters | null)?.price_min;
          const max = (pendingFilters as PendingFilters | null)?.price_max;
          if (min == null && max == null) return [];
          return [
            min != null ? String(min) : "",
            max != null ? String(max) : "",
          ];
        }
        return (
          ((pendingFilters as Record<string, unknown>)?.[field] as
            | string[]
            | null
            | undefined) ?? []
        );
      },
      onSelectionChange: (field, values) => {
        if (field === "price") {
          const [min, max] = values;
          updatePendingFilter(
            "price_min",
            min?.trim() ? parseFloat(min) : null,
          );
          updatePendingFilter(
            "price_max",
            max?.trim() ? parseFloat(max) : null,
          );
          return;
        }
        updatePendingFilter(field as "size", values);
      },
    }),
    [pendingFilters, updatePendingFilter],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

export function useFilterContext(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (!ctx)
    throw new Error("useFilterContext must be used within a filter provider");
  return ctx;
}
